import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import * as echarts from 'echarts';
import { DeepDiveService, PricingActionRecord } from './deep-dive.service';

const ALL = 'All';

@Component({
  selector: 'app-deep-dive',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './deep-dive.component.html',
  styleUrl: './deep-dive.component.scss',
})
export class DeepDiveComponent implements OnInit, AfterViewInit, OnDestroy {
  private service = inject(DeepDiveService);
  private route = inject(ActivatedRoute);
  private hostElement: ElementRef<HTMLElement> = inject(ElementRef);

  @ViewChild('chart') chartEl!: ElementRef<HTMLDivElement>;
  private chart?: echarts.ECharts;
  private viewReady = false;

  /**
   * History records for the current dropdown selection, sorted chronologically
   * (oldest → newest) so the chart's time axis reads left to right.
   */
  filtered: PricingActionRecord[] = [];

  /** Table rows: reverse-chronological so the latest pricing action shows first. */
  get historyRows(): PricingActionRecord[] {
    return [...this.filtered].reverse();
  }

  get canDownloadDeepDive(): boolean {
    return !this.isLoading && this.historyRows.length > 0;
  }

  // Dropdown state
  marketingOptions: string[] = [ALL];
  technicalOptions: string[] = [ALL];
  fuelOptions: string[] = [ALL];
  selectedMarketing = ALL;
  selectedTechnical = ALL;
  selectedFuel = ALL;
  openDropdownKey: 'marketing' | 'technical' | 'fuel' | null = null;
  dropdownSearchTerms: Record<'marketing' | 'technical' | 'fuel', string> = {
    marketing: '',
    technical: '',
    fuel: '',
  };

  isLoading = true;

  readonly columns: { key: keyof PricingActionRecord; label: string }[] = [
    { key: 'marketing_configuration', label: 'Marketing Config' },
    { key: 'technical_configuration', label: 'Tech Config' },
    { key: 'fuel_rating_hp', label: 'FR' },
    { key: 'price', label: 'Price ($)' },
    { key: 'effective_date', label: 'Effective Date' },
    { key: 'pricing_action', label: 'Pricing Action' },
    { key: 'change_reason', label: 'Change Reason' },
    { key: 'notes', label: 'Notes' },
  ];

  ngOnInit(): void {
    this.loadDropdownOptions();
  }

  /**
   * Dropdown values come from the `ReportDropDown` API — one call per column.
   * Each dropdown is preselected from the route query params when the page is
   * opened via the Pricing Action icon (`?technical_configuration=…` etc.),
   * otherwise it falls back to `All`.
   * The history for the resulting selection is then loaded.
   */
  private loadDropdownOptions(): void {
    const params = this.route.snapshot.queryParamMap;

    this.service.getInitialData().subscribe({
      next: ({ marketing, technical, fuel, records }) => {
        this.marketingOptions = [ALL, ...marketing];
        this.technicalOptions = [ALL, ...technical];
        this.fuelOptions = [ALL, ...fuel];

        this.selectedMarketing = this.resolveInitialSelection(
          params.get('marketing_configuration'),
          this.marketingOptions,
        );
        this.selectedTechnical = this.resolveInitialSelection(
          params.get('technical_configuration'),
          this.technicalOptions,
        );
        this.selectedFuel = this.resolveInitialSelection(
          params.get('fuel_rating_hp'),
          this.fuelOptions,
        );

        const hasPreselectedFilter =
          this.selectedMarketing !== ALL ||
          this.selectedTechnical !== ALL ||
          this.selectedFuel !== ALL;

        if (hasPreselectedFilter) {
          this.loadHistory();
        } else {
          this.setRecords(records);
        }
      },
      error: () => this.setRecords([]),
    });
  }

  /**
   * Picks the initial dropdown value: a query param (from the Pricing Action
   * icon) takes precedence and is added to the option list if the API did not
   * return it; otherwise it falls back to `All`.
   */
  private resolveInitialSelection(param: string | null, allOptions: string[]): string {
    const requested = param?.trim();
    if (requested) {
      if (!allOptions.includes(requested)) {
        allOptions.push(requested);
      }
      return requested;
    }
    return ALL;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.chart = echarts.init(this.chartEl.nativeElement);
    this.renderChart();
    window.addEventListener('resize', this.handleResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
    this.chart?.dispose();
  }

  private handleResize = (): void => this.chart?.resize();

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openDropdownKey = null;
  }

  /** Called on every dropdown change — reloads the history for the new selection. */
  onFilterChange(): void {
    this.loadHistory();
  }

  toggleDropdown(key: 'marketing' | 'technical' | 'fuel', event: MouseEvent): void {
    event.stopPropagation();
    this.openDropdownKey = this.openDropdownKey === key ? null : key;
    if (this.openDropdownKey === key) {
      setTimeout(() => this.focusDropdownSearch(key));
    }
  }

  private focusDropdownSearch(key: 'marketing' | 'technical' | 'fuel'): void {
    const inputs = this.hostElement.nativeElement.querySelectorAll<HTMLInputElement>(
      '[data-dropdown-search]',
    );
    Array.from(inputs).find((input) => input.dataset['dropdownSearch'] === key)?.focus();
  }

  isDropdownOpen(key: 'marketing' | 'technical' | 'fuel'): boolean {
    return this.openDropdownKey === key;
  }

  setDropdownSearchTerm(key: 'marketing' | 'technical' | 'fuel', term: string): void {
    this.dropdownSearchTerms[key] = term ?? '';
  }

  getFilteredOptions(key: 'marketing' | 'technical' | 'fuel'): string[] {
    const term = this.dropdownSearchTerms[key].trim().toLowerCase();
    const options = this.getOptionsForKey(key);
    if (!term) {
      return options;
    }
    return options.filter((option) => option.toLowerCase().includes(term));
  }

  selectOption(key: 'marketing' | 'technical' | 'fuel', value: string): void {
    if (key === 'marketing') {
      this.selectedMarketing = value;
    } else if (key === 'technical') {
      this.selectedTechnical = value;
    } else {
      this.selectedFuel = value;
    }

    this.openDropdownKey = null;
    this.dropdownSearchTerms[key] = '';
    this.onFilterChange();
  }

  getSelectedValue(key: 'marketing' | 'technical' | 'fuel'): string {
    if (key === 'marketing') {
      return this.selectedMarketing;
    }
    if (key === 'technical') {
      return this.selectedTechnical;
    }
    return this.selectedFuel;
  }

  isOptionSelected(key: 'marketing' | 'technical' | 'fuel', option: string): boolean {
    return this.getSelectedValue(key) === option;
  }

  hasSelectedValue(key: 'marketing' | 'technical' | 'fuel'): boolean {
    return this.getSelectedValue(key) !== ALL;
  }

  removeSelectedOption(
    key: 'marketing' | 'technical' | 'fuel',
    event: Event,
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (key === 'marketing') {
      this.selectedMarketing = ALL;
    } else if (key === 'technical') {
      this.selectedTechnical = ALL;
    } else {
      this.selectedFuel = ALL;
    }
    this.dropdownSearchTerms[key] = '';
    this.onFilterChange();
  }

  private getOptionsForKey(key: 'marketing' | 'technical' | 'fuel'): string[] {
    if (key === 'marketing') {
      return this.marketingOptions;
    }
    if (key === 'technical') {
      return this.technicalOptions;
    }
    return this.fuelOptions;
  }

  /**
   * Fetches the pricing-action history for the current dropdown selection and
   * maps it onto the chart and table. Records are sorted chronologically for the
   * chart; the table reads them reverse-chronologically via `historyRows`.
   */
  private loadHistory(): void {
    this.isLoading = true;
    this.service
      .getHistory(
        {
          marketing_configuration: this.selectedMarketing === ALL ? null : this.selectedMarketing,
          technical_configuration: this.selectedTechnical === ALL ? null : this.selectedTechnical,
          fuel_rating_hp: this.selectedFuel === ALL ? null : this.selectedFuel,
        },
      )
      .subscribe({
        next: (records) => this.setRecords(records),
        error: () => this.setRecords([]),
      });
  }

  private setRecords(records: PricingActionRecord[]): void {
    this.filtered = [...records].sort((a, b) => a.effective_date.localeCompare(b.effective_date));
    this.isLoading = false;
    this.renderChart();
  }

  downloadDeepDiveCsv(): void {
    const rows = this.historyRows;
    if (!rows.length || !this.columns.length) return;

    const csvRows = [
      this.columns.map((col) => this.escapeCsvValue(col.label)).join(','),
      ...rows.map((row) =>
        this.columns
          .map((col) => this.escapeCsvValue(this.getCsvCellValue(row, col.key)))
          .join(','),
      ),
    ];
    const csv = csvRows.join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'RER Insights Report.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  private getCsvCellValue(row: PricingActionRecord, key: keyof PricingActionRecord): any {
    if (key === 'effective_date') {
      return this.formatDateForTable(row.effective_date);
    }
    return this.formatCellValue(row[key], key);
  }

  /** Hides the API sentinel used when a record has no fuel rating. */
  formatCellValue(value: PricingActionRecord[keyof PricingActionRecord], key: keyof PricingActionRecord): string | number {
    if (key === 'fuel_rating_hp' && String(value ?? '').trim().toUpperCase() === 'EMPTY') {
      return '';
    }
    return value ?? '';
  }

  private escapeCsvValue(value: any): string {
    if (value == null) return '';
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  }

  private renderChart(): void {
    if (!this.viewReady || !this.chart) return;

    // No data → clear the canvas so ECharts doesn't draw empty axes (the
    // "Price ($)" name and axis line); the "No pricing actions…" overlay stands alone.
    if (this.filtered.length === 0) {
      this.chart.clear();
      return;
    }

    // Chart plots chronologically (oldest → newest); the table below lists the
    // same records reverse-chronologically via `historyRows`.
    const categories = this.filtered.map((r) => this.formatDate(r.effective_date));
    const values = this.filtered.map((r) => r.price);
    const accent = '#c4312b';

    const n = values.length;
    let markLine: any;
    let markPoint: any;
    if (n >= 2) {
      const last = values[n - 1];
      const prev = values[n - 2];
      const pct = prev !== 0 ? ((last - prev) / prev) * 100 : 0;
      const up = pct >= 0;
      const pctText = `${up ? '▲' : '▼'} ${up ? '+' : ''}${Math.round(pct)}%`;
      const pctColor = up ? '#16a34a' : '#dc2626';
      markLine = {
        silent: true,
        symbol: 'none',
        lineStyle: { type: 'dashed', color: '#9ca3af', width: 1 },
        label: { show: false },
        data: [
          [{ coord: [n - 2, prev] }, { coord: [n - 1, prev] }],
          [{ coord: [n - 2, last] }, { coord: [n - 1, last] }],
          // Left vertical guide bounded to the two point levels (no overflow).
          [{ coord: [n - 2, prev] }, { coord: [n - 2, last] }],
        ],
      };
      markPoint = {
        silent: true,
        data: [
          {
            coord: [n - 1, (prev + last) / 2],
            symbol: 'triangle',
            symbolRotate: up ? 0 : 180,
            symbolSize: 11,
            symbolOffset: [12, 0],
            itemStyle: { color: pctColor },
            label: { show: false },
          },
          {
            coord: [n - 1, last],
            symbol: 'roundRect',
            symbolSize: [52, 24],
            symbolOffset: [52, 0],
            itemStyle: {
              color: '#ffffff',
              borderColor: pctColor,
              borderWidth: 1.5,
              shadowBlur: 6,
              shadowColor: 'rgba(0,0,0,0.18)',
              shadowOffsetY: 2,
            },
            label: { show: true, position: 'inside', formatter: pctText, color: pctColor, fontWeight: 700, fontSize: 11 },
          },
        ],
      };
    }

    this.chart.setOption(
      {
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            const p = Array.isArray(params) ? params[0] : params;
            const row = this.filtered[p.dataIndex];
            if (!row) return '';
            return `<ul style="margin:0;padding-left:16px;">
              <li>Price: <b>$${row.price}</b></li>
              <li>Date: ${this.formatDate(row.effective_date)}</li>
            </ul>`;
          },
        },
        grid: { left: '3%', right: '12%', top: '12%', bottom: '6%', containLabel: true },
        xAxis: {
          type: 'category',
          data: categories,
          boundaryGap: true,
          axisTick: { alignWithLabel: true },
        },
        yAxis: { type: 'value', name: 'Price ($)', scale: true },
        series: [
          {
            type: 'line',
            data: values,
            smooth: false,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: { width: 3, color: accent, shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.15)', shadowOffsetY: 4 },
            itemStyle: { color: accent, borderColor: '#ffffff', borderWidth: 2 },
            emphasis: { scale: 1.5, focus: 'series' },
            label: { show: true, position: 'top', color: '#374151', fontWeight: 600, fontSize: 11 },
            ...(markLine ? { markLine } : {}),
            ...(markPoint ? { markPoint } : {}),
          },
        ],
      },
      true,
    );
    this.chart.resize();
  }

  private formatDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private formatDateForTable(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US');
  }
}
